import { toastManager } from '@systemeio/components'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next/pages'
import { likePost, unlikePost } from 'modules/community/api/community-api'
import { communityPostKeys } from 'modules/community/api/key-factory'
import type { PostInterface } from 'modules/community/types/post-interface'

export function useToggleLike(): UseMutationResult<
  Awaited<ReturnType<typeof likePost>>,
  Error,
  Pick<PostInterface, 'id' | 'isLiked'>
> {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: communityPostKeys.toggleLike(),
    mutationFn: ({ id, isLiked }: Pick<PostInterface, 'id' | 'isLiked'>) =>
      isLiked ? likePost(id) : unlikePost(id),
    onSuccess: async () => {
      await queryClient.refetchQueries(
        { queryKey: communityPostKeys.all, fetchStatus: 'fetching' },
        { cancelRefetch: false },
      )
      await queryClient.invalidateQueries(
        { queryKey: communityPostKeys.all },
        { cancelRefetch: false },
      )
    },
    onError: () => {
      toastManager.add({ type: 'error', title: t('post_details.error.error_like_post') })
    },
  })
}
